import { isEmpty } from 'fp-ts/Record';
import { useCallback, useEffect, useState } from 'react';
import { useInterval } from 'usehooks-ts';
import { ActionType } from '@mews-commander/enums';

/** Interval to check find forms to register */
const FORM_CHECK_INTERVAL = 500;

/** Interval to refetch form data */
const FORM_UPDATE_INTERVAL = 500;

export interface ObservedFormState {
    element: HTMLFormElement;
    initialValues: Record<string, FormDataEntryValue>;
    values: Record<string, FormDataEntryValue>;
    state: { isDirty: boolean };
}

const defaultFormState = { isDirty: false };

/**
 * EXPERIMENTAL
 * Finds all the forms on the page on an interval, stopping when found.
 * Creates a list of forms with their value and state.
 *  @deprecated as this is a temporary, hacky solution to prove if it has a positive impact on TTO
 */
export function useFormObserver() {
    // We ignore if the page has multiple forms, we only need the values and if they have been changed
    const [formList, setFormList] = useState<ObservedFormState[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isDeleteAction, setIsDeleteAction] = useState(false);

    const reset = useCallback(() => {
        setFormList([]);
        setIsInitialized(false);
        setIsDeleteAction(false);
    }, []);

    const updateFormData = useCallback(
        (form: HTMLFormElement, values: Record<string, FormDataEntryValue>) =>
            setFormList((prevFormList) =>
                prevFormList.map((oldForm) => {
                    if (oldForm.element !== form) {
                        return oldForm;
                    }
                    return {
                        ...oldForm,
                        state: { ...oldForm.state, isDirty: !areFormsEqual(oldForm.initialValues, values) },
                        values,
                    };
                })
            ),
        []
    );

    useInterval(
        () => {
            const formNodeList = document.querySelectorAll<HTMLFormElement>('form');
            const formListArr = Array.from(formNodeList);
            if (formNodeList.length === 0) {
                return;
            }

            // Hacky solution for some BE forms
            // There are some BE forms that have a race condition on which they may return a empty initialValues
            // Forms in react usually return a non empty object (non filled inputs have an empty string as value)
            // In order to fix this, we check if the forms returns empty, if so, we wait for the next interval
            const areFormsPending = formListArr.some((formEl) => isEmpty(createFormObject(formEl)));

            if (areFormsPending) {
                return;
            }

            const formListState = Array.from(formNodeList).map((formEl) => ({
                element: formEl,
                initialValues: createFormObject(formEl),
                values: createFormObject(formEl),
                state: defaultFormState,
            }));

            setFormList(formListState);
            setIsInitialized(true);
        },
        isInitialized ? null : FORM_CHECK_INTERVAL
    );

    useInterval(
        () => {
            formList.forEach((form) => {
                // If the form gets disconnected from the DOM, reset the state
                if (!document.body.contains(form.element)) {
                    reset();
                    return;
                }

                const values = createFormObject(form.element);
                if (!areFormsEqual(form.values, values)) {
                    updateFormData(form.element, values);
                }
            });
        },
        isInitialized ? FORM_UPDATE_INTERVAL : null
    );

    useInterval(
        () => {
            // used to opt out of the unsaved changes dialog when there's a delete confirmation dialog open
            // relies on the conventions of using /Delete routes
            const confirmationDialogOpen =
                document.querySelector(
                    `[data-action-type="${ActionType.ConfirmationDialog}"][data-action-url*="/Delete"]`
                ) != null;
            if (isDeleteAction !== confirmationDialogOpen) {
                setIsDeleteAction(confirmationDialogOpen);
            }
        },
        isInitialized ? FORM_UPDATE_INTERVAL : null
    );

    useEffect(() => {
        formList.forEach((form) => form.element.addEventListener('submit', reset));

        return () => {
            formList.forEach((form) => form.element.removeEventListener('submit', reset));
        };
    }, [formList, reset]);

    const hasDirtyForms = formList.some((form) => form.state.isDirty);

    return {
        formList,
        hasDirtyForms,
        reset,
        isDeleteAction,
    };
}

function createFormObject(form: HTMLFormElement) {
    // The __AntiforgeryToken is not needed for comparison and can lead to false positives
    const { __AntiforgeryToken, ...otherData } = Object.fromEntries(new FormData(form));
    return otherData;
}

function areFormsEqual(formA: Record<string, FormDataEntryValue>, formB: Record<string, FormDataEntryValue>) {
    if (Object.entries(formA).length !== Object.entries(formB).length) {
        return false;
    }

    // We need to shallow compare excluding the File objects, which we compare only the name and size
    for (const key in formA) {
        if (formA[key] instanceof File && formB[key] instanceof File) {
            if (formA[key].name !== formB[key].name || formA[key].size !== formB[key].size) {
                return false;
            }
        } else if (formA[key] !== formB[key]) {
            return false;
        }
    }

    return true;
}
